import Controller from '@ember/controller';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency';
import type { IntlService } from 'ember-intl';
import type { LinkManagerService } from 'ember-link';
import type { ShowcaseContentTab } from 'walden-fast/components/showcase-content';
import type { Tab } from 'walden-fast/components/tabs';
import type { AppServicesWaldenGreenModel } from 'walden-fast/routes/app/services/walden-green';
import FastApiService from 'walden-fast/services/fast-api';
import UserService from 'walden-fast/services/user';
import { type ClientCompany } from 'walden-fast/types/fast-backend/user';
import { type YearlyEmissionsReport } from 'walden-fast/types/yearly-emissions-report';
import type { Stat } from 'walden-fast/types/stats';
import downloadFile from 'walden-fast/utils/download-file';

type Range = 'mtd' | 'ytd';

export default class AppServicesWaldenGreenController extends Controller {
  declare model: AppServicesWaldenGreenModel;

  @service('intl') declare intlService: IntlService;
  @service('link-manager') declare linkManager: LinkManagerService;
  @service('fast-api') declare fastApiService: FastApiService;
  @service('user') declare userService: UserService;

  // 1) make it a query-param so it survives refresh & can be used by child routes
  queryParams = ['numbersDataRange'];

  @tracked numbersDataRange: Range = 'mtd';
  @tracked reportError = false;

  // 2) build a single “stats” array, merging your original KPI stats +
  //    a dynamic conversion of EmissionsAggregated[ range ]
  get stats(): Stat[] {
    const { kpiStats, emissionsAggregated } = this.model;

    // if no data yet, just fall back to pure KPI stats
    if (!emissionsAggregated || !Object.keys(emissionsAggregated)) {
      return kpiStats;
    }

    const rangeKey =
      this.numbersDataRange === 'mtd' ? 'monthToDate' : 'yearToDate';

    const rangeLabelMap = {
      mtd: this.intlService.t('pages.waldenGreen.stats.range.mtd'),
      ytd: this.intlService.t('pages.waldenGreen.stats.range.ytd'),
    } as const;
    const rangeLabel = rangeLabelMap[this.numbersDataRange];

    const emissionsStats: Stat[] = [
      {
        label: this.intlService.t(
          'pages.waldenGreen.stats.totalNumberOfShipments',
          {
            range: rangeLabel,
          },
        ),
        value:
          emissionsAggregated.totalNumberOfShipments[rangeKey].toLocaleString(
            'nl-BE',
          ),
        unit: 'shipments',
      },
      {
        label: this.intlService.t('pages.waldenGreen.stats.totalCO2Tonnes', {
          range: rangeLabel,
        }),
        value: emissionsAggregated.totalCo2Tonnes[rangeKey].toFixed(3),
        unit: 'tonnes',
      },
      {
        label: this.intlService.t('pages.waldenGreen.stats.totalDistance', {
          range: rangeLabel,
        }),
        value:
          emissionsAggregated.totalDistance[rangeKey].toLocaleString('nl-BE'),
        unit: 'km',
      },
    ];

    return [...kpiStats, ...emissionsStats];
  }

  get tabs(): Tab[] {
    return [
      {
        internalLink: this.linkManager.createLink({
          route: 'app.services.walden-green.overview',
        }),
        label: this.intlService.t('pages.waldenGreen.tabs.overview'),
        type: 'internal',
      },
    ];
  }

  get showcaseTabs(): ShowcaseContentTab[] {
    return [
      {
        label: this.intlService.t('pages.waldenGreen.teaser.tabInsights.label'),
        value: 'insights',
        iconName: 'cloud-fog',
        title: this.intlService.t('pages.waldenGreen.teaser.tabInsights.title'),
        description: this.intlService.t(
          'pages.waldenGreen.teaser.tabInsights.description',
        ),
        image: '/assets/images/green_teaser_1.png',
        primaryCta: {
          type: 'activate-service',
          label: this.intlService.t('pages.waldenGreen.teaser.getWaldenGreen'),
          value: '',
        },
      },
      {
        label: this.intlService.t('pages.waldenGreen.teaser.tabOffset.label'),
        value: 'offset',
        iconName: 'footprints',
        title: this.intlService.t('pages.waldenGreen.teaser.tabOffset.label'),
        description: this.intlService.t(
          'pages.waldenGreen.teaser.tabOffset.description',
        ),
        image: '/assets/images/green_teaser_2.png',
        primaryCta: {
          type: 'activate-service',
          label: this.intlService.t('pages.waldenGreen.teaser.getWaldenGreen'),
          value: '',
        },
      },
      {
        label: this.intlService.t('pages.waldenGreen.teaser.tabOptimise.label'),
        value: 'optimise',
        iconName: 'replace',
        title: this.intlService.t('pages.waldenGreen.teaser.tabOptimise.label'),
        description: this.intlService.t(
          'pages.waldenGreen.teaser.tabOptimise.description',
        ),
        image: '/assets/images/green_teaser_3.png',
        primaryCta: {
          type: 'activate-service',
          label: this.intlService.t('pages.waldenGreen.teaser.getWaldenGreen'),
          value: '',
        },
      },
      {
        label: this.intlService.t('pages.waldenGreen.teaser.tabGrow.label'),
        value: 'grow',
        iconName: 'sprout',
        title: this.intlService.t('pages.waldenGreen.teaser.tabGrow.label'),
        description: this.intlService.t(
          'pages.waldenGreen.teaser.tabGrow.description',
        ),
        image: '/assets/images/green_teaser_4.png',
        primaryCta: {
          type: 'activate-service',
          label: this.intlService.t('pages.waldenGreen.teaser.getWaldenGreen'),
          value: '',
        },
      },
    ];
  }

  get clients() {
    return this.userService.user ? this.userService.user.clientCompanies : [];
  }

  get years() {
    return [2024];
  }

  @action
  hideError() {
    this.reportError = false;
  }

  downloadReport = task(async (year: number, client: ClientCompany) => {
    try {
      this.reportError = false;

      const result = await this.fastApiService.get<YearlyEmissionsReport>(
        '/walden-green/reports/yearly-emissions-report',
        {
          year,
          clientCompanyId: client.id,
        },
      );

      if (result?.url) {
        await downloadFile(result.url, result.fileName);
      } else {
        throw new Error('No report URL received');
      }

      return result;
    } catch {
      this.reportError = true;
    }
  });
}
